/**
 * 홍보 광고 신청 — 플랜·규정·Firestore·로컬 미리보기·토스 빌링
 * @see docs/AD_APPLICATION_SPEC.md
 */

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";

// ——— 토스 (광고 빌링) ———

const DOC_TEST_CLIENT_KEY = "test_ck_Gv6LjeKD8aXEEBv5A4KBrwYxAdXy";

export const TOSS_CLIENT_KEY =
  (process.env.VUE_APP_TOSS_CLIENT_KEY || "").trim() || DOC_TEST_CLIENT_KEY;

export function buildAdBillingCustomerKey(userStore) {
  const raw = userStore?.uid || userStore?.email || "guest";
  const safe = String(raw).replace(/[^a-zA-Z0-9_@=.-]/g, "_").slice(0, 40);
  return `lk_ad_${safe}`;
}

// ——— 플랜 ———

export const AD_PLAN_LIST = [
  {
    id: "basic",
    name: "베이직",
    badge: null,
    monthlyAmount: 20000,
    vat: 2000,
    totalAmount: 22000,
    maxImpressions: null,
    description: "홈 하단·상세 페이지 광고 슬롯 월정액",
    highlights: [
      "홈(메인) 하단 광고 영역 노출",
      "라이브·아티스트·장소·영상·게시글 등 상세 페이지 하단 노출",
      "노출/클릭/CTR 기본 리포트",
      "매월 자동 결제 · 언제든 해지 가능",
    ],
  },
];

export const AD_PLAN_MAP = AD_PLAN_LIST.reduce((acc, plan) => {
  acc[plan.id] = plan;
  return acc;
}, {});

export const DEFAULT_AD_PLAN_ID = "basic";

export function buildPlanSnapshot(planId) {
  const plan = AD_PLAN_MAP[planId];
  if (!plan) return null;
  return {
    id: plan.id,
    name: plan.name,
    monthlyAmount: plan.monthlyAmount,
    vat: plan.vat,
    totalAmount: plan.totalAmount,
    maxImpressions: plan.maxImpressions,
    description: plan.description,
  };
}

export function formatWon(value) {
  const v = Number(value) || 0;
  return `${new Intl.NumberFormat("ko-KR").format(v)}원`;
}

// ——— 취소·환불 규정 ———

export const AD_CANCEL_REFUND_POLICY = [
  {
    title: "1. 정기결제 해지",
    items: [
      "「내 광고」에서 언제든 정기결제 해지를 신청할 수 있습니다.",
      "해지 신청 시 익월부터 자동 결제가 중단되며, 광고 노출은 현재 결제 기간 종료일까지 유지됩니다.",
      "이미 결제·이용이 시작된 당월 분에 대한 환불은 원칙적으로 제공되지 않습니다.",
    ],
  },
  {
    title: "2. 심사 반려·미게재",
    items: [
      "관리자 심사 결과 광고 소재가 부적합하여 반려된 경우, 첫 결제 금액은 전액 환불합니다.",
      "심사 통과 후 링꾹라이브 측 사유로 정상 게재가 불가능한 경우, 미이용 일수 기준으로 일할 환불하거나 전액 환불할 수 있습니다.",
    ],
  },
  {
    title: "3. 환불이 제한되는 경우",
    items: [
      "심사 승인 후 정상적으로 광고가 노출된 이후, 광고주 사유로 해지하는 경우",
      "광고주가 제공한 링크·소재 오류, 허위·불법 콘텐츠 등으로 인한 노출 중단",
      "이미 제공된 광고 집행 기간(당월)에 대한 부분 환불 요청 (단, 회사 귀책 사유는 제외)",
    ],
  },
  {
    title: "4. 환불 절차 및 기간",
    items: [
      "환불 요청: 카카오톡 「링꾹」 비즈니스 채널 또는 신청 시 기재한 담당자 이메일",
      "환불 승인 후 카드사 정책에 따라 환불 완료까지 약 3~10영업일이 소요될 수 있습니다.",
      "환불 금액은 실제 결제 금액(공급가·VAT 포함 합계) 기준으로 처리합니다.",
    ],
  },
  {
    title: "5. 기타",
    items: [
      "본 규정에 명시되지 않은 사항은 링꾹라이브 이용약관 및 관련 법령(전자상거래법 등)에 따릅니다.",
      "허위·과장 광고, 타인 권리 침해 등 운영 정책 위반 시 사전 통보 없이 노출 중단·계약 해지될 수 있으며, 이 경우 환불이 제한될 수 있습니다.",
    ],
  },
];

export const AD_CANCEL_REFUND_SUMMARY =
  "해지 시 당월 분은 만료일까지 노출이 유지되며 환불되지 않습니다. 심사 반려 시 첫 결제는 전액 환불됩니다.";

// ——— 로컬 미리보기 (API·Firestore 실패 시 sessionStorage) ———

const DRAFT_KEY_PREFIX = "adApplicationDraft:";

export function isLocalPreviewCampaignId(id) {
  return String(id || "").startsWith("preview-");
}

export function createLocalPreviewCampaignId() {
  return `preview-${Date.now()}`;
}

export function saveLocalAdDraft(campaignId, payload, extra = {}) {
  const data = {
    id: campaignId,
    status: "draft",
    isLocalPreview: true,
    ...payload,
    savedAt: Date.now(),
    ...extra,
  };
  sessionStorage.setItem(`${DRAFT_KEY_PREFIX}${campaignId}`, JSON.stringify(data));
  return data;
}

export function getLocalAdDraft(campaignId) {
  if (!campaignId) return null;
  try {
    const raw = sessionStorage.getItem(`${DRAFT_KEY_PREFIX}${campaignId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function removeLocalAdDraft(campaignId) {
  if (campaignId) {
    sessionStorage.removeItem(`${DRAFT_KEY_PREFIX}${campaignId}`);
  }
}

// ——— Firestore / Storage ———

const CAMPAIGNS = "adCampaigns";
const PAYMENTS = "adPayments";
const BANNERS = "banners";

function campaignsCol() {
  return collection(firestore, CAMPAIGNS);
}

function campaignRef(id) {
  return doc(firestore, CAMPAIGNS, id);
}

function paymentsCol() {
  return collection(firestore, PAYMENTS);
}

function resolveOwner() {
  const userStore = useUserStore();
  const ownerId = userStore.uid;
  if (!ownerId) {
    const err = new Error("광고 신청은 Firebase 로그인(UID)이 필요합니다.");
    err.code = "NO_UID";
    throw err;
  }
  return {
    ownerId,
    ownerEmail: userStore.email || "",
    ownerNickname: userStore.nickname || null,
  };
}

function currentOwnerId() {
  return resolveOwner().ownerId;
}

function serializeCampaign(id, data) {
  if (!data) return null;
  return {
    id,
    ...data,
    createdAt: data.createdAt?.toDate?.() ? data.createdAt.toDate() : data.createdAt,
    updatedAt: data.updatedAt?.toDate?.() ? data.updatedAt.toDate() : data.updatedAt,
    currentPeriodStart: data.currentPeriodStart?.toDate?.()
      ? data.currentPeriodStart.toDate()
      : data.currentPeriodStart,
    currentPeriodEnd: data.currentPeriodEnd?.toDate?.()
      ? data.currentPeriodEnd.toDate()
      : data.currentPeriodEnd,
    nextBillingAt: data.nextBillingAt?.toDate?.() ? data.nextBillingAt.toDate() : data.nextBillingAt,
    lastPaymentAt: data.lastPaymentAt?.toDate?.() ? data.lastPaymentAt.toDate() : data.lastPaymentAt,
    canceledAt: data.canceledAt?.toDate?.() ? data.canceledAt.toDate() : data.canceledAt,
  };
}

function assertOwner(docSnap, ownerId) {
  if (!docSnap.exists()) {
    const err = new Error("캠페인을 찾을 수 없습니다.");
    err.code = "NOT_FOUND";
    throw err;
  }
  const data = docSnap.data();
  if (data.ownerId !== ownerId) {
    const err = new Error("권한이 없습니다.");
    err.code = "FORBIDDEN";
    throw err;
  }
  return data;
}

function buildCampaignPayload(payload, owner) {
  const customerKey = buildAdBillingCustomerKey({ uid: owner.ownerId, email: owner.ownerEmail });
  return {
    slot: "detail",
    plan: payload.plan || "basic",
    planSnapshot: payload.planSnapshot || null,
    advertiser: payload.advertiser || {},
    material: payload.material || {},
    ownerId: owner.ownerId,
    ownerEmail: owner.ownerEmail || "",
    ownerNickname: owner.ownerNickname || null,
    billing: {
      customerKey,
      billingKey: null,
      cardCompany: null,
      cardNumberMasked: null,
      issuedAt: null,
    },
    bannerId: null,
    currentPeriodStart: null,
    currentPeriodEnd: null,
    nextBillingAt: null,
    lastPaymentAt: null,
    lastPaymentStatus: null,
    rejectReason: null,
    pauseReason: null,
    cancelReason: null,
    canceledAt: null,
    updatedAt: serverTimestamp(),
  };
}

async function fsCreateAdCampaign(payload, owner) {
  const body = {
    ...buildCampaignPayload(payload, owner),
    status: "draft",
    createdAt: serverTimestamp(),
  };
  const refDoc = await addDoc(campaignsCol(), body);
  return { id: refDoc.id, campaignId: refDoc.id };
}

async function fsUpdateAdCampaign(campaignId, payload, ownerId) {
  const snap = await getDoc(campaignRef(campaignId));
  const existing = assertOwner(snap, ownerId);
  if (existing.status && existing.status !== "draft") {
    const err = new Error("임시저장 상태의 신청서만 수정할 수 있습니다.");
    err.code = "NOT_DRAFT";
    throw err;
  }
  await updateDoc(campaignRef(campaignId), {
    plan: payload.plan ?? existing.plan,
    planSnapshot: payload.planSnapshot ?? existing.planSnapshot,
    advertiser: payload.advertiser ?? existing.advertiser,
    material: payload.material ?? existing.material,
    updatedAt: serverTimestamp(),
  });
  return { id: campaignId };
}

async function fsGetAdCampaign(campaignId, ownerId, { isAdmin = false } = {}) {
  if (isLocalPreviewCampaignId(campaignId)) {
    return getLocalAdDraft(campaignId);
  }
  const snap = await getDoc(campaignRef(campaignId));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (!isAdmin && data.ownerId !== ownerId) {
    const err = new Error("권한이 없습니다.");
    err.code = "FORBIDDEN";
    throw err;
  }
  return serializeCampaign(snap.id, data);
}

async function fsListMyAdCampaigns(ownerId) {
  const q = query(
    campaignsCol(),
    where("ownerId", "==", ownerId),
    orderBy("updatedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => serializeCampaign(d.id, d.data()));
}

async function fsUploadAdMaterialImage(campaignId, imageFile, ownerId, onProgress) {
  const snap = await getDoc(campaignRef(campaignId));
  assertOwner(snap, ownerId);

  const ext = (imageFile.name.split(".").pop() || "jpg").toLowerCase();
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const storagePath = `adMaterials/${campaignId}/${fileName}`;
  const fileRef = ref(storage, storagePath);

  await uploadBytes(fileRef, imageFile);
  if (onProgress) onProgress(100);

  const url = await getDownloadURL(fileRef);
  const material = {
    ...(snap.data().material || {}),
    imageUrl: url,
    imageFileName: fileName,
  };
  await updateDoc(campaignRef(campaignId), {
    material,
    updatedAt: serverTimestamp(),
  });
  return { url, downloadURL: url, fileName };
}

async function fsPrepareAdBilling(campaignId, ownerId) {
  const snap = await getDoc(campaignRef(campaignId));
  const data = assertOwner(snap, ownerId);
  const planSnapshot = data.planSnapshot || {};
  const customerKey =
    data.billing?.customerKey || buildAdBillingCustomerKey({ uid: ownerId, email: data.ownerEmail });

  await updateDoc(campaignRef(campaignId), {
    status: "pending_payment",
    "billing.customerKey": customerKey,
    updatedAt: serverTimestamp(),
  });

  const orderId = `lk-ad-${campaignId}-${Date.now()}`.slice(0, 60);
  const amount = Number(planSnapshot.totalAmount) || 0;
  const orderName = `링꾹라이브 광고 ${planSnapshot.name || "베이직"}`;

  return { customerKey, orderId, orderName, amount };
}

async function fsConfirmAdBillingIssue({ campaignId, customerKey, authKey }, ownerId) {
  const snap = await getDoc(campaignRef(campaignId));
  const data = assertOwner(snap, ownerId);
  const planSnapshot = data.planSnapshot || {};
  const now = new Date();
  const periodEnd = new Date(now);
  periodEnd.setMonth(periodEnd.getMonth() + 1);

  await updateDoc(campaignRef(campaignId), {
    status: "pending_review",
    "billing.customerKey": customerKey,
    "billing.pendingAuthKey": authKey,
    "billing.authReceivedAt": serverTimestamp(),
    currentPeriodStart: Timestamp.fromDate(now),
    currentPeriodEnd: Timestamp.fromDate(periodEnd),
    nextBillingAt: Timestamp.fromDate(periodEnd),
    lastPaymentAt: serverTimestamp(),
    lastPaymentStatus: "card_registered",
    updatedAt: serverTimestamp(),
  });

  await addDoc(paymentsCol(), {
    campaignId,
    ownerId,
    amount: planSnapshot.monthlyAmount || 0,
    vat: planSnapshot.vat || 0,
    totalAmount: planSnapshot.totalAmount || 0,
    method: "billing",
    paymentKey: null,
    orderId: `lk-ad-${campaignId}-${now.getTime()}`.slice(0, 60),
    status: "card_registered",
    failReason: null,
    cardCompany: null,
    cardNumberMasked: null,
    periodStart: Timestamp.fromDate(now),
    periodEnd: Timestamp.fromDate(periodEnd),
    paidAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  return { ok: true, campaignId, status: "pending_review" };
}

async function fsCancelAdSubscription(campaignId, reason, ownerId) {
  const snap = await getDoc(campaignRef(campaignId));
  const data = assertOwner(snap, ownerId);
  const allowed = ["active", "pending_review", "paused", "pending_payment"];
  if (!allowed.includes(data.status)) {
    const err = new Error("해지할 수 없는 상태입니다.");
    err.code = "INVALID_STATUS";
    throw err;
  }
  await updateDoc(campaignRef(campaignId), {
    status: "canceled",
    cancelReason: reason || "",
    canceledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { ok: true, status: "canceled" };
}

async function fsListAdCampaignPayments(campaignId, ownerId) {
  const campSnap = await getDoc(campaignRef(campaignId));
  assertOwner(campSnap, ownerId);

  const q = query(
    paymentsCol(),
    where("campaignId", "==", campaignId),
    orderBy("paidAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      ...data,
      paidAt: data.paidAt?.toDate?.() ? data.paidAt.toDate() : data.paidAt,
      periodStart: data.periodStart?.toDate?.() ? data.periodStart.toDate() : data.periodStart,
      periodEnd: data.periodEnd?.toDate?.() ? data.periodEnd.toDate() : data.periodEnd,
    };
  });
}

function buildMonthlyFromBanner(banner) {
  const clicksByDate = banner.clicksByDate || {};
  const impressionsByDate = banner.impressionsByDate || {};
  const months = new Set([...Object.keys(clicksByDate), ...Object.keys(impressionsByDate)]);
  return [...months]
    .sort()
    .map((key) => {
      const [y, m] = key.split("-").map(Number);
      return {
        yearMonth: `${y}-${String(m).padStart(2, "0")}`,
        impressions: Number(impressionsByDate[key]) || 0,
        clicks: Number(clicksByDate[key]) || 0,
      };
    });
}

async function fsGetAdCampaignMetrics(campaignId, ownerId) {
  const campaign = await fsGetAdCampaign(campaignId, ownerId);
  if (!campaign?.bannerId) {
    return {
      totalImpressions: 0,
      totalClicks: 0,
      ctr: 0,
      periodDays: 0,
      monthly: [],
      currentPeriod: campaign?.currentPeriodStart
        ? { start: campaign.currentPeriodStart, end: campaign.currentPeriodEnd }
        : null,
    };
  }

  const bannerSnap = await getDoc(doc(firestore, BANNERS, campaign.bannerId));
  if (!bannerSnap.exists()) {
    return {
      totalImpressions: 0,
      totalClicks: 0,
      ctr: 0,
      monthly: [],
      currentPeriod: null,
    };
  }

  const banner = bannerSnap.data();
  const totalImpressions = Number(banner.impressionCount) || 0;
  const totalClicks = Number(banner.clickCount) || 0;
  const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  return {
    totalImpressions,
    totalClicks,
    ctr,
    periodDays: null,
    monthly: buildMonthlyFromBanner(banner),
    currentPeriod: campaign.currentPeriodStart
      ? { start: campaign.currentPeriodStart, end: campaign.currentPeriodEnd }
      : null,
  };
}

// ——— Public API ———

export async function listMyAdCampaigns() {
  try {
    return await fsListMyAdCampaigns(currentOwnerId());
  } catch (error) {
    console.error("내 광고 캠페인 목록 조회 오류:", error);
    throw error;
  }
}

export async function getAdCampaign(campaignId) {
  try {
    return await fsGetAdCampaign(campaignId, currentOwnerId());
  } catch (error) {
    console.error("광고 캠페인 조회 오류:", error);
    throw error;
  }
}

export async function listAdCampaignPayments(campaignId) {
  try {
    return await fsListAdCampaignPayments(campaignId, currentOwnerId());
  } catch (error) {
    console.error("광고 캠페인 결제내역 조회 오류:", error);
    throw error;
  }
}

export async function getAdCampaignMetrics(campaignId) {
  try {
    return await fsGetAdCampaignMetrics(campaignId, currentOwnerId());
  } catch (error) {
    console.error("광고 캠페인 성과 조회 오류:", error);
    throw error;
  }
}

export async function createAdCampaignDraft(payload) {
  try {
    return await fsCreateAdCampaign(payload, resolveOwner());
  } catch (error) {
    console.error("광고 캠페인 draft 생성 오류:", error);
    throw error;
  }
}

export async function updateAdCampaignDraft(campaignId, updateData) {
  try {
    const { ownerId } = resolveOwner();
    return await fsUpdateAdCampaign(campaignId, updateData, ownerId);
  } catch (error) {
    console.error("광고 캠페인 수정 오류:", error);
    throw error;
  }
}

export async function uploadAdMaterialImage(campaignId, imageFile, onProgress = null) {
  try {
    const { ownerId } = resolveOwner();
    return await fsUploadAdMaterialImage(campaignId, imageFile, ownerId, onProgress);
  } catch (error) {
    console.error("광고 소재 이미지 업로드 오류:", error);
    throw error;
  }
}

export async function prepareAdBilling(campaignId) {
  try {
    const { ownerId } = resolveOwner();
    return await fsPrepareAdBilling(campaignId, ownerId);
  } catch (error) {
    console.error("광고 빌링 준비 오류:", error);
    throw error;
  }
}

export async function confirmAdBillingIssue(payload) {
  try {
    const { ownerId } = resolveOwner();
    return await fsConfirmAdBillingIssue(payload, ownerId);
  } catch (error) {
    console.error("광고 빌링 확인 저장 오류:", error);
    throw error;
  }
}

export async function cancelAdSubscription(campaignId, reason = "") {
  try {
    const { ownerId } = resolveOwner();
    return await fsCancelAdSubscription(campaignId, reason, ownerId);
  } catch (error) {
    console.error("정기결제 해지 오류:", error);
    throw error;
  }
}
